import type { Id } from '@hq/utils';
import { toQuery, type HttpClient } from '../client';
import type { PageQuery, PageResult } from '../types/result';

/** 调价单状态（`wl_material_price.approval_status`，由审批引擎回写）。 */
export type PriceChangeStatus = 'DRAFT' | 'APPROVING' | 'APPROVED' | 'REJECTED' | 'CANCELED' | string;

/** 调价单（后端 PriceChangeRow）。`change_no` 非空的行才是有单号的调价申请。 */
export interface PriceChangeRow {
  id: Id;
  changeNo: string | null;
  materialId: Id;
  materialCode: string | null;
  materialName: string | null;
  spec: string | null;
  supplierId: Id;
  /** 供应商名称（后端按主数据回填）。 */
  supplierName?: string | null;
  orgId: Id;
  priceType: string;
  taxKind: string;
  taxRate: number;
  price: number;
  unitId: Id;
  minQty: number;
  effectiveFrom: string;
  effectiveTo: string | null;
  /** 提交日到生效日的自然天数（招标要求 ≥30）。 */
  noticeDays: number;
  applyAt: string | null;
  instanceId: Id;
  approvalStatus: PriceChangeStatus;
  changeReason: string | null;
  changeFileUrl: string | null;
  approvedBy: Id;
  approvedAt: string | null;
  createAt: string;
}

export interface PriceChangeInstance {
  id: Id;
  bizNo: string;
  bizTitle: string | null;
  flowId: Id;
  flowVersion: number;
  instanceStatus: string;
  currentNodeCode: string | null;
  currentNodeName: string | null;
  startUserName: string | null;
  startAt: string | null;
  endAt: string | null;
  durationMinutes: number;
  rejectReason: string | null;
}

export interface PriceChangeTask {
  task_id: Id;
  node_code: string;
  node_name: string | null;
  assignee_name: string | null;
  task_status: string;
  action: string | null;
  opinion: string | null;
  handle_at: string | null;
  due_at: string | null;
}

export interface PriceChangeLog {
  node_code: string | null;
  from_status: string | null;
  to_status: string | null;
  action: string;
  actor_name: string | null;
  opinion: string | null;
  act_at: string;
}

/** `GET /api/wl/material-price/change/{id}` → `{ price, instance, tasks, logs }`。 */
export interface PriceChangeDetail {
  price: PriceChangeRow;
  instance: PriceChangeInstance | null;
  tasks: PriceChangeTask[];
  logs: PriceChangeLog[];
}

/** 提交调价（后端 PriceChangeCreate；生效日须距今天 ≥30 天）。 */
export interface PriceChangeCreateRequest {
  materialId: Id;
  supplierId: Id;
  /** 缺省为当前用户机构。 */
  orgId?: Id;
  priceType?: string;
  taxKind?: 'INCLUSIVE' | 'EXCLUSIVE';
  /** 0~1，例如 0.13。 */
  taxRate: number;
  price: number;
  unitId: Id;
  minQty: number;
  /** `yyyy-MM-dd`（后端取 .Date）。 */
  effectiveFrom: string;
  /** 不少于 5 字，需与书面申请一致。 */
  changeReason: string;
  changeFileUrl?: string | null;
}

export type PriceChangeAction = 'APPROVE' | 'REJECT';

export interface PriceChangeDecideRequest {
  action: PriceChangeAction;
  opinion?: string | null;
}

/** 审批动作结果（提交返回更全：含 changeNo/instanceId/noticeDays）。 */
export interface PriceChangeDecisionResult {
  id: Id;
  approvalStatus: string;
  nodeCode: string | null;
  nodeName: string | null;
  pending: number;
  approvers: string[];
  /** 仅提交接口返回。 */
  changeNo?: string;
  instanceId?: Id;
  noticeDays?: number;
  effectiveFrom?: string;
}

export interface PriceChangeListQuery extends PageQuery {
  approvalStatus?: string;
  materialId?: Id;
  supplierId?: Id;
}

/** 物资调价申请（招标"后勤物资采购"第 6 条：提前 1 个月书面申请 + 采购人核实）。 */
export function createPriceChangeApi(http: HttpClient) {
  return {
    list(query: PriceChangeListQuery = {}): Promise<PageResult<PriceChangeRow>> {
      return http.request<PageResult<PriceChangeRow>>({ path: '/wl/material-price/change', query: toQuery(query) });
    },
    detail(id: Id): Promise<PriceChangeDetail> {
      return http.request<PriceChangeDetail>({ path: `/wl/material-price/change/${id}` });
    },
    /** 提交调价申请并发起审批（幂等）。 */
    submit(body: PriceChangeCreateRequest): Promise<PriceChangeDecisionResult> {
      return http.request<PriceChangeDecisionResult>({
        path: '/wl/material-price/change',
        method: 'POST',
        body
      });
    },
    /** 审批（同意/驳回），终审通过时自动关闭上一版价格。 */
    decide(id: Id, body: PriceChangeDecideRequest): Promise<PriceChangeDecisionResult> {
      return http.request<PriceChangeDecisionResult>({
        path: `/wl/material-price/change/${id}/decide`,
        method: 'POST',
        body
      });
    }
  };
}

export type PriceChangeApi = ReturnType<typeof createPriceChangeApi>;
